import { MutableRefObject, useCallback } from "react";
import { useDispatch } from "react-redux";

import actions from "@/actions";
import { FreeDrawText } from "@/sx-layout/components/plotmap/components/freeDraw/models";

type Props = {
  moveable: boolean;
  selectedText?: FreeDrawText;
  updateSelectedText: (text: FreeDrawText) => void;
  textRef: MutableRefObject<HTMLDivElement>;
  imageRef: MutableRefObject<HTMLImageElement>;
  scale: number;
  mapRatio: number;
  setIsDragging: (_: boolean) => void;
};

export const useMoveableText = ({
  moveable,
  selectedText,
  updateSelectedText,
  textRef,
  imageRef,
  scale,
  mapRatio,
  setIsDragging,
}: Props) => {
  const dispatch = useDispatch();

  const onDrag = useCallback(
    ({ beforeTranslate }: { beforeTranslate: number[] }) => {
      if (!moveable) return;

      const target = textRef.current;
      const imageRect = imageRef.current.getBoundingClientRect();

      // X軸の制限
      let x = beforeTranslate[0];
      const targetLeft = parseInt(target.style.left);
      const targetWidth = target.offsetWidth;
      const imageWidth = imageRect.width * scale;
      const moveX = targetLeft + beforeTranslate[0];
      if (moveX < 0) {
        x = -targetLeft;
      } else if (moveX + targetWidth > imageWidth) {
        x = imageWidth - targetLeft - targetWidth;
      }

      // Y軸の制限
      let y = beforeTranslate[1];
      const targetTop = parseInt(target.style.top);
      const targetHeight = target.offsetHeight;
      const imageHeight = imageRect.height * scale;
      const moveY = targetTop + beforeTranslate[1];
      if (moveY < 0) {
        y = -targetTop;
      } else if (moveY + targetHeight > imageHeight) {
        y = imageHeight - targetTop - targetHeight;
      }

      setIsDragging(true);
      target.style.transform = `translate(${x}px, ${y}px)`;
    },
    [imageRef, textRef, moveable, scale, setIsDragging]
  );

  const getTranslateXY = (element: HTMLElement) => {
    const style = window.getComputedStyle(element);
    const matrix = new DOMMatrixReadOnly(style.transform);

    return {
      translateX: matrix.m41,
      translateY: matrix.m42,
    };
  };

  const onDragEnd = useCallback(
    ({ isDrag }: { isDrag: boolean }) => {
      if (!moveable) return;

      const target = textRef.current;
      if (isDrag) {
        setIsDragging(false);
        const { translateX, translateY } = getTranslateXY(target);
        const textX = Math.round((parseInt(target.style.left) + translateX) / mapRatio);
        const textY = Math.round((parseInt(target.style.top) + translateY) / mapRatio);

        dispatch(
          actions.texts.updateTextCoordinate({
            input: {
              freetext_id: selectedText.freetext_id,
              x: textX,
              y: textY,
              timestamp: {
                update_date: selectedText.timestamp?.update_date,
              },
            },
            onSuccess: (text: FreeDrawText) => {
              target.style.transform = null;
              updateSelectedText({
                ...selectedText,
                x: textX,
                y: textY,
                timestamp: {
                  ...selectedText.timestamp,
                  update_date: text.timestamp?.update_date,
                },
              });
            },
          })
        );
      }
    },
    [
      dispatch,
      textRef,
      mapRatio,
      moveable,
      selectedText?.freetext_id,
      selectedText?.timestamp,
      setIsDragging,
      updateSelectedText,
    ]
  );

  return {
    onDrag,
    onDragEnd,
  };
};
